import styled, { CSSProperties } from "styled-components";
import media from "styled-media-query";

import { Text, ColorV2 } from "@adoptaunabuelo/react-components";
import { useEffect, useState } from "react";
import { CornerDownRight } from "lucide-react";
import { State } from "../../redux";
import { useSelector } from "react-redux";

const ZigzagView = styled.div<{ style?: CSSProperties }>`
	display: flex;
	flex-direction: column;
	position: relative;
	box-sizing: border-box;
	padding: 32px 64px;
	background-color: ${(props) =>
		props.style?.backgroundColor
			? props.style.backgroundColor
			: ColorV2.surface.background};
	min-width: 350px;
	max-width: 100%;
	width: 100%;
	gap: 8px;
	&:before,
	&:after {
		content: "";
		position: absolute;
		left: 0px;
		height: 8px;
		width: 100%;
	}
	&:before {
		top: -5px;
		background: radial-gradient(
				circle,
				transparent,
				transparent 50%,
				${(props) =>
						props.style?.backgroundColor
							? props.style.backgroundColor
							: ColorV2.surface.background}
					50%,
				${(props) =>
						props.style?.backgroundColor
							? props.style.backgroundColor
							: ColorV2.surface.background}
					100%
			)
			4px -8px / 20px 15px repeat-x;
	}
	&:after {
		bottom: -5px;
		background: radial-gradient(
				circle,
				transparent,
				transparent 50%,
				${(props) =>
						props.style?.backgroundColor
							? props.style.backgroundColor
							: ColorV2.surface.background}
					50%,
				${(props) =>
						props.style?.backgroundColor
							? props.style.backgroundColor
							: ColorV2.surface.background}
					100%
			)
			4px 0px / 20px 20px repeat-x;
	}
	${media.lessThan("small")`
        padding: 24px;
        min-width: unset;
        width: 100%;
    `}
`;
const Cell = styled.div`
	display: flex;
	flex-direction: row;
`;
const Image = styled.img`
	height: 48px;
	width: 48px;
	border-radius: 6px;
	object-fit: cover;
	margin-right: 16px;
`;
const Row = styled.div`
	display: flex;
	flex: 1;
	flex-direction: row;
	align-items: center;
`;
const Line = styled.div`
	display: flex;
	flex: 1;
	border-bottom: 1px dashed ${ColorV2.surface.neutralLow};
	margin: 0px 16px;
	min-width: 12px;
	${media.lessThan("small")`
        margin: 0px 8px;
		min-width: 12px;
    `}
`;

const PriceBreakdownCell = (props: Props) => {
	const appLocation = useSelector((state: State) => state.location.location);
	const [goal, setGoal] = useState(0);

	useEffect(() => {
		let temp = 0;
		props.options.map((item) => {
			if (typeof item.amount.eur === "number") {
				return (temp = temp + item.amount.eur);
			}
			return 0;
		});
		setGoal(temp);
	}, [props.options]);

	const calculateAmount = (amount: number | string): string => {
		if (typeof amount === "number") {
			const tempAmount =
				amount / (appLocation ? appLocation.currency.conversion : 1);
			const tempCurrencyName = appLocation
				? appLocation.currency.name
				: "eur";
			const tempLanguage = appLocation ? appLocation.language : "es-ES";

			return new Intl.NumberFormat(tempLanguage, {
				style: "currency",
				currency: tempCurrencyName,
				maximumFractionDigits: 2,
			}).format(tempAmount);
		} else return amount;
	};

	return (
		<ZigzagView style={props.style}>
			{props.options.map((item, index) => (
				<div key={"distribution-" + index}>
					<Cell
						style={{
							justifyContent: "space-between",
						}}
					>
						{item.image && <Image src={item.image} />}
						<Row
							style={{
								minHeight: item.image ? 48 : 32,
								alignItems: "flex-end",
							}}
						>
							<Text
								type="p"
								style={{
									fontFamily: "DM Mono",
									backgroundColor: item.backgroundColor,
								}}
							>
								{item.title}
							</Text>
							{!item.items && (
								<>
									<Line />
									<Text
										type="p"
										style={{
											fontFamily: "DM Mono",
											whiteSpace: "nowrap",
										}}
									>
										{calculateAmount(item.amount.eur)}
									</Text>
								</>
							)}
						</Row>
					</Cell>
					{item.items?.map((subitem, index) => (
						<Row
							key={"sub-distribution-" + index}
							style={{
								minHeight: item.image ? 48 : 32,
								alignItems: "flex-end",
							}}
						>
							<CornerDownRight
								height={16}
								width={16}
								style={{ marginRight: 8, marginLeft: 16 }}
							/>
							<Text
								type="p"
								style={{
									fontFamily: "DM Mono",
									display: "flex",
									marginRight: 24,
								}}
							>
								{subitem.title}
							</Text>
							<Line />
							<Text type="p" style={{ fontFamily: "DM Mono" }}>
								{calculateAmount(subitem.amount.eur)}
							</Text>
						</Row>
					))}
				</div>
			))}
			{!props.hideTotal && (
				<Cell
					style={{
						justifyContent: "space-between",
						paddingTop: 12,
						marginTop: 12,
					}}
				>
					<Text
						type="h6"
						style={{
							fontFamily: "DM Mono",
							fontWeight: 500,
							display: "flex",
							flex: 1,
						}}
					>
						TOTAL
					</Text>
					<Text
						type="h6"
						style={{ fontFamily: "DM Mono", fontWeight: 500 }}
					>
						{calculateAmount(goal)}
					</Text>
				</Cell>
			)}
		</ZigzagView>
	);
};
export default PriceBreakdownCell;
export interface Props {
	style?: any;
	title?: string;
	hideTotal?: boolean;
	options: Array<{
		title: string | React.ReactElement;
		image?: string;
		backgroundColor?: string;
		amount: {
			[key: string]: number | string;
		};
		items?: Array<{
			title: string;
			amount: {
				[key: string]: number;
			};
		}>;
	}>;
}
